<template>
  <div class="credit-card-form">
    <v-radio-group v-model="omiseCardId" hide-details class="pt-0">
      <v-card v-if="isTokenLoading" class="mb-4">
        <v-skeleton-loader
        type="list-item"
        />
      </v-card>
      <v-card v-else v-for="(o, i) in omiseTokenList" :key="o.id" class="px-4 py-2 mb-4">
        <v-radio
          :value="o.id"
        >
          <template slot="label">
            <div class="d-flex justify-space-between align-center flex-grow-1">
              <div class="d-flex align-center">
                <span class="text--primary font-weight-light">{{ o.brand }}</span>
                <!-- <img width="40" :srcSet="getBrandImgSrc(o.brand)" class="mr-2" /> -->
                <!-- <span class="text--primary font-weight-light">{{ o.bank }}</span> -->
              </div>
              <div class="d-flex align-center">
                <div class="font-weight-light text-right">{{ 'XXXX XXXX XXXX ' + o.last_digits }}</div>
                <v-progress-circular
                  v-if="omiseTokenIsDeleting[i]"
                  indeterminate
                  :size="20"
                  :width="2"
                  class="ml-4 mr-n2 my-2"
                ></v-progress-circular>
                <v-menu v-else offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on" :ripple="false" class="ml-2 mr-n4">
                      <v-icon>$mdiDotsVertical</v-icon>
                    </v-btn>
                  </template>

                  <v-list>
                    <v-list-item @click="removeOmiseCard(i)">
                      <v-list-item-title>{{ $t('app.delete') }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </div>
          </template>
        </v-radio>
      </v-card>
      <v-card v-if="!isTokenLoading" class="pa-4">
        <v-radio value="new" class="mb-3">
          <template slot="label">
            <div class="d-flex justify-space-between align-center flex-grow-1">
              <span class="text--primary font-weight-light">New Credit/Debit Card</span>
            </div>
          </template>
        </v-radio>
        <v-form ref="form">
          <div class="mb-2 d-flex justify-space-between align-end">
            <div class="text--secondary">Card number</div>
            <img :srcSet="utility.getImgSrcSet('ic_creditcard' + ($vuetify.theme && $vuetify.theme.dark ? '_dark' : ''))" />
          </div>
          <div class="input-container px-3 mb-4" :class="formErrors.cardNumber && formDirty.cardNumber ? 'has-error':''">
            <input :value="cardNumber" class="text--primary width-full" type="tel" @input="updateCardNumber"/>
          </div>
          <div class="text--secondary mb-2">Name on card</div>
          <div class="input-container px-3 mb-4" :class="formErrors.nameOnCard && formDirty.nameOnCard ? 'has-error':''">
            <input v-model="nameOnCard" class="text--primary width-full" @input="formDirty.nameOnCard = true"/>
          </div>
          <div class="d-flex">
            <div class="mr-4">
              <div class="text--secondary mb-2">Expiration date</div>
              <div class="input-container px-3" :class="formErrors.expiration && formDirty.expiration ? 'has-error':''">
                <input :value="expiration" placeholder="MM/YY" class="text--primary width-full" @input="updateExpiration"/>
              </div>
            </div>
            <div>
              <div class="text--secondary mb-2">Security code</div>
              <div class="input-container d-flex px-3" :class="formErrors.securityCode && formDirty.securityCode ? 'has-error':''">
                <input v-model="securityCode" type="password" autocomplete="cc-number" maxlength="3" class="text--primary width-full" @input="formDirty.securityCode = true"/>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" class="text--disabled">$mdiHelpCircle</v-icon>
                  </template>
                  <span>3-digit number on the back of your credit card</span>
                </v-tooltip>
              </div>
            </div>
          </div>
          <!-- Save for later use -->
          <!-- hide if the form is used for subscription -->
          <v-switch
            v-if="!(orderForm.packageId || orderForm.subscribeId)"
            hide-details
            color="primary"
            v-model="saveForLater"
            inset
            class="mt-3 mb-0"
            :label="'Save for later'"
          ></v-switch>
        </v-form>
      </v-card>
    </v-radio-group>
    <v-btn depressed color="primary" class="btn-full width-full mt-4 mb-4"
    :loading="isPlacingOrder || isCreatingToken"
    @click="submitForm">
      <span v-if="$route.name === 'PackageCreditChange'">{{ $t('app.confirm') }}</span>
      <span v-else-if="orderForm.price">{{ $t('app.pay') }} {{ utility.toFixedDecimal(orderForm.price) }} {{ orderForm.currency }}</span>
      <span v-else>{{ $t('app.pay') }}</span>
    </v-btn>
    <OmiseFooter/>
    <div
      v-if="terms"
      id="omise-terms"
      class="text-pre-wrap text-sm text--secondary font-weight-light"
      v-html="terms"
    />
  </div>
</template>

<script>
import OmiseFooter from '@/components/OmiseFooter.vue'

export default {
  name: 'CreditCardForm',
  components: {
    OmiseFooter
  },
  props: {
    orderForm: {
      type: Object,
      default: null
    },
    isPlacingOrder: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      token: localStorage.getItem('token'),
      deviceId: sessionStorage.getItem('uuid') ? null : localStorage.getItem('deviceId'),
      uuid: sessionStorage.getItem('uuid'),
      omiseCardId: null,
      omiseTokenList: [],
      cardNumber: '',
      nameOnCard: '',
      expiration: '',
      securityCode: '',
      saveForLater: false,
      isCreatingToken: false,
      formDirty: {
        cardNumber: false,
        nameOnCard: false,
        expiration: false,
        securityCode: false
      },
      isTokenLoading: false,
      terms: null
    }
  },
  computed: {
    omiseTokenIsDeleting: function () {
      const ar = this.omiseTokenList.map(x => !!x.isLoading)
      return ar
    },
    formErrors: function () {
      return {
        cardNumber: !this.cardNumber || this.cardNumber.length !== 19,
        nameOnCard: !this.nameOnCard,
        expiration: !this.expiration || this.expiration.length !== 5,
        securityCode: !this.securityCode || this.securityCode.length !== 3
      }
    },
    hasErrors: function () {
      const keys = Object.keys(this.formErrors)
      for (const i in keys) {
        const k = keys[i]
        if (this.formErrors[k]) return true
      }
      return false
    }
  },
  mounted () {
    this.getOmiseCards()
  },
  methods: {
    getBrandImgSrc (brand) {
      return this.utility.getImgSrcSet('ic_creditcard' + (this.$vuetify.theme && this.$vuetify.theme.dark ? '_dark' : ''))
    },
    submitForm () {
      if (this.omiseCardId && this.omiseCardId !== 'new') {
        this.$emit('on-omise-ready', this.omiseCardId, null, null)
      } else {
        this.validateForm()
      }
    },
    validateForm () {
      this.formDirty = {
        cardNumber: true,
        nameOnCard: true,
        expiration: true,
        securityCode: true
      }
      if (!this.hasErrors) {
        this.createOmiseToken()
      }
    },
    updateCardNumber (e) {
      const reg = /^\d+$/
      const val = e.target.value.split('-').join('')
      if (!val.length || (val.length <= 16 && reg.test(val))) {
        this.cardNumber = val.length ? val.match(/.{1,4}/g).join('-') : ''
        this.formDirty.cardNumber = true
      }
      this.$forceUpdate()
    },
    updateExpiration (e) {
      const reg = /^\d+$/
      let val = e.target.value.split('/').join('')
      if (!val.length || (val.length <= 4 && reg.test(val))) {
        if (val.length === 1 && parseInt(val) > 1) val = '0' + val
        else if (val.length === 2 && parseInt(val) > 12) val = '12'
        this.expiration = val.length ? val.match(/.{1,2}/g).join('/') : ''
        this.formDirty.expiration = true
      }
      this.$forceUpdate()
    },
    createOmiseToken () {
      const self = this
      const expiration = this.expiration.split('/')
      const card = {
        name: this.nameOnCard,
        number: this.cardNumber.split('-').join(''),
        expiration_month: expiration[0],
        expiration_year: expiration[1],
        security_code: this.securityCode
      }
      this.isCreatingToken = true
      const omisePublicToken = this.orderForm.payment ? this.orderForm.payment.omisePublicToken : this.orderForm.omisePublicToken
      window.Omise.setPublicKey(omisePublicToken)
      window.Omise.createToken('card', card, function (statusCode, response) {
        if (response.object === 'error') {
          console.warn(response)
          self.dialogWarning.commit({
            type: 'show',
            message: 'Payment Error',
            detail: response.message
          })
          self.isCreatingToken = false
        } else {
          self.$emit('on-omise-ready', null, response.id, self.saveForLater)
          self.isCreatingToken = false
        }
      })
    },
    // ajax
    getOmiseCards () {
      this.isTokenLoading = true
      this.api.getOmiseCards({
        locale: this.$i18n.locale,
        token: this.token,
        deviceId: this.deviceId,
        uuid: this.uuid,
        cardId: this.$route.params.cardId
      })
        .then(response => {
          switch (response.data.code) {
            case 200:
              this.omiseTokenList = response.data.data
              // this.$nextTick(() => {
              if (this.omiseTokenList && this.omiseTokenList.length && this.orderForm.omiseCardId) {
                if (this.omiseTokenList.map(x => x.id).indexOf(this.orderForm.omiseCardId) !== -1) {
                  this.omiseCardId = this.orderForm.omiseCardId
                } else {
                  this.omiseCardId = 'new'
                }
              } else {
                this.omiseCardId = (this.omiseTokenList && this.omiseTokenList.length) ? this.omiseTokenList[0].id : 'new'
              }
              // })
              this.terms = response.data.terms
              break
            default:
              this.dialogWarning.commit({
                type: 'show',
                message: response.data.msg ? response.data.msg : 'Unknown Error',
                detail: null
              })
              break
          }
        }).catch((error) => {
          console.warn(error)
          this.dialogWarning.commit({
            type: 'show',
            message: 'Connection Error',
            detail: null
          })
        }).finally(() => {
          this.isTokenLoading = false
        })
    },
    removeOmiseCard (i) {
      this.$set(this.omiseTokenList[i], 'isLoading', true)
      const omiseCardId = this.omiseTokenList[i].id
      // send ajax
      this.api.removeOmiseCard({
        locale: this.$i18n.locale,
        token: this.token,
        deviceId: this.deviceId,
        uuid: this.uuid,
        cardId: this.$route.params.cardId,
        omiseCardId
      })
        .then(response => {
          switch (response.data.code) {
            case 200:
              // update view after request success
              this.omiseTokenList.splice(i, 1)
              if (this.omiseCardId === omiseCardId) {
                this.omiseCardId = (this.omiseTokenList && this.omiseTokenList.length) ? this.omiseTokenList[0].id : 'new'
              }
              break
            default:
              this.$set(this.omiseTokenList[i], 'isLoading', false)
              this.dialogWarning.commit({
                type: 'show',
                message: response.data.msg ? response.data.msg : 'Unknown Error',
                detail: null
              })
              break
          }
        }).catch((error) => {
          this.$set(this.omiseTokenList[i], 'isLoading', false)
          console.warn(error)
          this.dialogWarning.commit({
            type: 'show',
            message: 'Connection Error',
            detail: null
          })
        })
    }
  }
}
</script>

<style lang="scss">
  .credit-card-form {
    .v-input--switch .v-input__slot {
      flex-direction: row-reverse;
    }
  }
</style>
